import {
    Badge,
    Button,
    Collapse,
    Divider,
    Dropdown,
    Form,
    Icon,
    Input,
    InputNumber,
    Menu,
    Modal,
    Popconfirm,
    Tooltip, Typography
} from "antd";
import {BASE_CLS} from "./util";
import React, {useCallback, useContext} from "react";
import {DepDestListItem, Phase2EntityFormData, Phase2ScheduleNode} from "./types";
import ETable, {ETableProps} from "../../../../components/enchanced-antd-table";
import {PassengerEditor} from "../../../../components/PassengerEditor";
import NonIdealState from "../../../../components/NonIdealState";
import MCIcon from "../../../../components/icon";
import {stopPropagationProps} from "../../../../common/util-ts";
import {Location} from "../../../../common/types/location";
import {useHistory} from "react-router-dom";
import {approximateLegRouteColumnWidth, LegRoute} from "../../../../components/flights/scheduling/util";
import {calcBagCount, calcBagWeight, calcTotalWeight} from "../../../../components/flights/scheduling/math";
import {getTransitType} from "../../../../common/carriable/util";
import * as NotesForPilot from "../../../../components/flights/scheduling/notes-for-pilot";
import {Phase2Api} from "./api";
import {ScheduledCgoNode, ScheduledPaxNode, ScheduleNode} from "../../../../components/scheduling/types";
import {getSNodeID} from "../../../../components/scheduling/util";
import {FormComponentProps} from "antd/lib/form";
import {createFormField, FormItemClassNames, getLabelInValue, LabelInValue} from "../../../../common/form";
import PaxNameCell from "common/person/pax-name-cell";
import {createChargeCodeValidator} from "./validation";
import {ColumnProps} from "antd/lib/table";
import ComplianceStatusButton from "../../../../components/compliance/compliance-status-button";
import {OrganizationSelect} from "../../../../components/form/select/OrganizationSelect";
import moment from "moment-timezone";
import SafetyCardStatus from "../../../../components/scheduling/safety-card-status";
import {OrgDataContext} from "../../../../context/orgData";
import {isTalosCustomer} from "../../../../common/constants/organizations";

export interface Phase2TableProps {
    containerElement?: HTMLElement,
    disableEditing?: boolean,
    allowSelection?: boolean,
    api: Phase2Api,
    modifyPaxColumns?: (api: Phase2Api, columns: ColumnProps<any>[]) => ColumnProps<any>[],
    modifyCgoColumns?: (api: Phase2Api, columns: ColumnProps<any>[]) => ColumnProps<any>[],
    onRow?: ETableProps['onRow']
}

type Phase2TablePropsWithForm = FormComponentProps<{ [entityId: string]: Phase2EntityFormData }>
    & Phase2TableProps

function createFormFieldKey(entityId: string, fieldName: string){
    return entityId + '::' + fieldName;
}

function splitFormFieldKey(formFieldKey: string){
    const elements = formFieldKey.split('::');

    if (elements.length == 2){
        return {
            entityId: elements[0],
            fieldName: elements[1]
        }
    }

    return null;
}

function NotesForPilotWrapper(props: {
    api: Phase2Api,
    entity: ScheduleNode,
    popupContainer: HTMLElement,
    disableEditing?: boolean
}){

    const { api, entity, popupContainer } = props;
    const note = entity.notesForPilot;
    const noteMap = api.data.getNotesForPilot();

    const entitiesWithNote = (note ? noteMap[note] : undefined) || [];
    const editMode = props.disableEditing ? false : api.editMode.get();

    const renderAccept: NotesForPilot.QuickEdit.QuickEditProps['renderAccept'] =
        ({ onConfirm, buttonProps }) => {

            function handleConfirm(updateAllWithNote?: boolean){

                if (!entity){
                    return;
                }

                let newEntities: ScheduleNode[] = [];

                function updateEntity(entity: ScheduleNode){
                    return {
                        ...entity,
                        notesForPilot: api.notesForPilot.getModifiedText()
                    }
                }

                if (updateAllWithNote){
                    const oldNote = entity.notesForPilot;

                    const noteMap = api.data.getNotesForPilot();
                    if (!noteMap[oldNote]){
                        return;
                    }

                    newEntities = noteMap[oldNote].map(updateEntity)
                }
                else
                {
                    newEntities = [updateEntity(entity)];
                }

                api.data.modifiedEntities.add(newEntities);
                onConfirm();
            }

        if (entitiesWithNote.length > 1){
            const overlay = (
                <Menu>
                    <Menu.Item
                        onClick={() => handleConfirm()}
                    >Update this item only</Menu.Item>
                    <Menu.Item
                        onClick={() => {
                            Modal.confirm({
                                title: 'Review affected items',
                                content: <>
                                    <p>The following PAX/CGO will have their pilot notes changed:</p>
                                    <ul>
                                        {entitiesWithNote.map(entity => {
                                            if (entity.classType === 'flytsuite.paxnode'){
                                                const pax = entity as ScheduledPaxNode;
                                                return <li>{pax.lastName}, {pax.firstName}</li>
                                            }
                                            else
                                            {
                                                const cgo = entity as ScheduledCgoNode;
                                                return <li>{cgo.name}</li>
                                            }
                                        })}
                                    </ul>
                                </>,
                                okText: `Update ${entitiesWithNote.length} items`,
                                onOk: () => handleConfirm(true)
                            })
                        }}
                    >Update {entitiesWithNote.length} items</Menu.Item>
                </Menu>
            )

            return (
                <Dropdown
                    overlay={overlay}
                    trigger={['click']}
                >
                    <Button
                        {...buttonProps}
                    />
                </Dropdown>
            )
        }

        return (
            <Button
                {...buttonProps}
                onClick={() => handleConfirm()}
            />
        );
    }

    const readOnly = Boolean(entity.currentCarrierID) || !editMode;

    return (
        <NotesForPilot.QuickEdit.QuickEdit
            readOnly={readOnly}
            scheduleNodeData={entity}
            popupContainer={popupContainer}
            renderAccept={renderAccept}
            placeholderText={readOnly ? '—' : undefined}
            {...api.notesForPilot.getQuickEditProps(getSNodeID(entity))}
        />
    )
}

const Phase2TableBase: React.FC<Phase2TablePropsWithForm> = (props) => {

    // Hooks -----------------------------------------------------------------------------------------------------------
    const { api } = props;
    const history = useHistory();
    const orgData = useContext(OrgDataContext);
    const showSafetyCard = isTalosCustomer(orgData?.customerID);
    // -----------------------------------------------------------------------------------------------------------------

    // Event handlers---------------------------------------------------------------------------------------------------
    function handleCellEdit(entityId: string, fieldKey: string, value: any) {
        api.data.modifyEntity(entityId, { [fieldKey]: value });
    }
    // -----------------------------------------------------------------------------------------------------------------

    // State helpers ---------------------------------------------------------------------------------------------------
    const hasSelectedPersonnel = !!api.selection.personnelIds.get().length;
    const editMode = props.disableEditing ? false : api.editMode.get();
    const selectionEnabled = editMode || props.allowSelection;
    // -----------------------------------------------------------------------------------------------------------------

    const data = api.data.getLegs();

    const entityCount = data.reduce((count, leg) => {
        return count + leg.all.length
    }, 0)

    // Utils -----------------------------------------------------------------------------------------------------------
    const legRouteColumnWidth = approximateLegRouteColumnWidth(
        data.map((item) => ({
            departureName: item.departure?.name,
            destinationName: item.destination?.name
        }))
    );
    // -----------------------------------------------------------------------------------------------------------------

    function renderPanel(item: DepDestListItem){

        const selectedItems = item.all
            .filter((entity) => api.selection.entityIds.get().includes(getSNodeID(api.toScheduleNode(entity))));

        const itemsOnFlight = item.all
            .filter((entity) => entity.currentCarrierID);

        const depName = item.departure?.name;
        const destName = item.destination?.name;

        const pax = item.pax;
        const cgo = item.cgo;

        const totWt = calcTotalWeight(item.all.map(api.toScheduleNode));
        const totPaxWt = calcTotalWeight(item.pax);
        const totCgoWt = calcTotalWeight(item.cgo);

        const totBagWt = calcBagWeight(item.pax);
        const totBagCt = calcBagCount(item.pax);

        let tooltipTitle = 'Click to show table';
        if (item.departure?._id && item.destination?._id && api.editor.visibleLegs.isLegVisible(item.departure._id, item.destination._id)){
            tooltipTitle = 'Click to hide table';
        }

        const tooltipContent = <>
            <p>{tooltipTitle}</p>
            <table className={BASE_CLS + '-table-collapse-panel-tooltip-table'}>
                <tbody>
                    <tr>
                        <th>PAX</th>
                        <td>{pax.length}</td>
                    </tr>
                    <tr>
                        <th>CGO</th>
                        <td>{cgo.length}</td>
                    </tr>
                    <tr>
                        <th>Transit</th>
                        <td>{getTransitType(item.departure, item.destination)}</td>
                    </tr>
                    <tr>
                        <th>Total Weight</th>
                        <td>{totWt}</td>
                    </tr>
                    <tr>
                        <th>Total PAX Weight</th>
                        <td>{totPaxWt}</td>
                    </tr>
                    <tr>
                        <th>Total CGO Weight</th>
                        <td>{totCgoWt}</td>
                    </tr>
                    <tr>
                        <th>Total Bag Weight</th>
                        <td>{totBagWt}</td>
                    </tr>
                    <tr>
                        <th>Total Bag Count</th>
                        <td>{totBagCt}</td>
                    </tr>
                </tbody>
            </table>
        </>

        const header = (
            <Tooltip
                className={
                    BASE_CLS + '-table-collapse-panel-header ' +
                    (selectedItems.length ? BASE_CLS + '-table-collapse-panel-tooltip-has-selected-personnel' : '')
                }
                title={tooltipContent}
                placement="leftTop"
                mouseEnterDelay={1}
            >
                {selectedItems.length ? (
                    <Tooltip title={`${selectedItems.length} selected`}>
                        <div className={BASE_CLS + '-table-collapse-panel-counter-wrapper'}>
                            <Badge
                                className={BASE_CLS + '-table-collapse-panel-counter'}
                                count={selectedItems.length}
                            />
                        </div>
                    </Tooltip>
                ) : null}
                {itemsOnFlight.length && !selectedItems.length ? (
                    <Tooltip title={`${itemsOnFlight.length} on flight`}>
                        <div className={BASE_CLS + '-table-collapse-panel-counter-wrapper'}>
                            <Badge
                                className={BASE_CLS + '-table-collapse-panel-counter'}
                                count={itemsOnFlight.length}
                            />
                        </div>
                    </Tooltip>
                ) : null}
                <div
                    className={BASE_CLS + '-table-collapse-panel-section'}
                    style={{
                        width: legRouteColumnWidth,
                        maxWidth: legRouteColumnWidth,
                        minWidth: legRouteColumnWidth
                    }}
                >
                    <LegRoute departureName={depName} destinationName={destName}/>
                </div>
                <div
                    className={BASE_CLS + '-table-collapse-panel-section'}
                    style={{minWidth: 110}}
                >
                    <span>{getTransitType(item.departure, item.destination)}</span>
                </div>
                <div
                    className={BASE_CLS + '-table-collapse-panel-section'}
                    style={{minWidth: 130}}
                >
                    <span><strong>{pax.length}</strong> PAX</span>
                    <Divider type="vertical"/>
                    <span><strong>{cgo.length}</strong> CGO</span>
                </div>
                <div
                    className={
                        BASE_CLS + '-table-collapse-panel-section ' +
                        BASE_CLS + '-table-collapse-panel-section-totals'
                    }
                    style={{minWidth: 410}}
                >
                    <strong style={{marginRight: '12px'}}>Totals:</strong>
                    <span>{totWt} Wt</span>
                    <Divider type="vertical"/>
                    <span>{totPaxWt} Pax Wt</span>
                    <Divider type="vertical"/>
                    <span>{totCgoWt} Cgo Wt</span>
                    <Divider type="vertical"/>
                    <span>{totBagWt} Bag Wt</span>
                    <Divider type="vertical"/>
                    <span>{totBagCt} Bag Ct</span>
                </div>
            </Tooltip>
        )

        const deleteBtn = (
            <Popconfirm
                title="Are you sure you want to remove this leg?"
                okText="Remove"
                okButtonProps={{
                    type: 'danger'
                }}
                onConfirm={() => {
                    api.data.removeLeg(item.departure._id, item.destination._id);
                }}
                placement="left"
            >
                <Button
                    type="danger"
                    icon="delete"
                    size="small"
                    ghost
                    onClick={(e) => {
                        e.stopPropagation();
                    }}
                />
            </Popconfirm>
        )

        let addBtn = null;
        if (hasSelectedPersonnel && api.carriableSelector.tabs.get().upperTab === 'personSearch'){
            addBtn = (
                <Button
                    type="primary"
                    icon="plus"
                    size="small"
                    style={{ marginRight: 6 }}
                    onClick={() => api.selection.personnelIds.addToSchedule(
                        item.departure._id,
                        item.destination._id
                    )}
                >Add {api.selection.personnelIds.get().length} PAX</Button>
            )
        }
        else if (api.carriableSelector.tabs.get().upperTab === 'cargoForm'){
            addBtn = (
                <Button
                    type="primary"
                    icon="plus"
                    size="small"
                    style={{ marginRight: 6 }}
                    onClick={(e) => {
                        e.stopPropagation();
                        api.forms.cargoForm.submit(
                            item.departure._id,
                            item.destination._id
                        )
                    }}
                >Add Cargo</Button>
            )
        }

        const reorderBtns = (
            <Button.Group
                style={{ marginRight: 6 }}
            >
                <Tooltip
                    title="Reorder down"
                    mouseEnterDelay={1}
                >
                    <Button
                        icon="arrow-down"
                        size="small"
                        disabled={item.rangeEnd >= entityCount-1}
                        onClick={(e) => {
                            e.stopPropagation();
                            api.data.swapLegs(item, 'down');
                        }}
                    />
                </Tooltip>
                <Tooltip
                    title="Reorder up"
                    mouseEnterDelay={1}
                >
                    <Button
                        icon="arrow-up"

                        size="small"
                        disabled={item.rangeStart === 0}
                        onClick={(e) => {
                            e.stopPropagation();
                            api.data.swapLegs(item, 'up');
                        }}
                    />
                </Tooltip>
            </Button.Group>
        )

        let moreBtn: JSX.Element;

        {
            const overlay = (
                <Menu>
                    <Menu.Item
                        disabled={selectedItems.length === item.all.length}
                        onClick={(clickParam) => {
                            clickParam.domEvent.stopPropagation();
                            api.editor.visibleLegs.show(item.departure?._id, item.destination?._id);
                            api.selection.entityIds.add(item.all.map(e => getSNodeID(api.toScheduleNode(e))));
                        }}
                    >
                        Select all
                    </Menu.Item>
                    <Menu.Item
                        disabled={selectedItems.length === 0}
                        onClick={(clickParam) => {
                            clickParam.domEvent.stopPropagation();
                            api.selection.entityIds.remove(item.all.map(e => getSNodeID(api.toScheduleNode(e))));
                        }}
                    >
                        De-select all
                    </Menu.Item>
                    <Divider type="horizontal" style={{ margin: 0 }} />
                    <Menu.Item
                        onClick={(clickParam) => {
                            clickParam.domEvent.stopPropagation();
                            const entityIds = item.all.map(e => getSNodeID(api.toScheduleNode(e)));
                            api.data.setEntityLegs(
                                entityIds,
                                item.destination,
                                item.departure
                            );
                        }}
                    >
                        Swap departure & destination
                    </Menu.Item>
                    <Divider type="horizontal" style={{ margin: 0 }} />
                    <Menu.Item
                        onClick={(clickParam) => {
                            clickParam.domEvent.stopPropagation();
                            const callback = (departure: Location, destination: Location) => {
                                api.data.setEntityLegs(
                                    item.all.map(e => getSNodeID(api.toScheduleNode(e))),
                                    departure,
                                    destination
                                )
                            }

                            api.modal.newLegFormModal.open(
                                item.departure,
                                item.destination,
                                callback
                            )
                        }}
                    >
                        Change departure & destination...
                    </Menu.Item>
                </Menu>
            )

            moreBtn = (
                <Dropdown
                    overlay={overlay}
                    trigger={['click']}
                >
                    <Button
                        icon="more"
                        size="small"
                        style={{
                            marginRight: 6
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                </Dropdown>
            )
        }

        let extra = [];

        if (editMode){
            extra = [
                addBtn,
                reorderBtns,
                moreBtn,
                deleteBtn
            ]
        }

        return <Collapse.Panel
            key={`${item.departure._id}::${item.destination._id}`}
            header={header}
            extra={extra}
            className={
                BASE_CLS + '-table-collapse-panel ' +
                (itemsOnFlight.length && !selectedItems.length ? BASE_CLS + '-table-collapse-panel-has-on-flight ' : '') +
                (selectedItems.length ? BASE_CLS + '-table-collapse-panel-has-selection' : '')
            }
        >
            <div className={BASE_CLS + '-table-container'}>
                <ETable.Group>
                    {renderPaxTable(item)}
                    {renderCgoTable(item)}
                </ETable.Group>
            </div>
        </Collapse.Panel>
    }

    const chargeCodeValidator = useCallback(createChargeCodeValidator(api), [api]);

    function renderPaxTable(item: DepDestListItem){

        let columns: ColumnProps<any>[] = [
            {
                title: <strong><i>PAX ({item.pax.length})</i></strong>,
                key: 'action',
                width: 73,
                fixed: 'left' as 'left',
                render: (_, row) => {

                    if (!editMode) return (
                        <div className={BASE_CLS + '-icon-container'}>
                            <MCIcon type="user" />
                        </div>
                    );

                    const reorderBtns = [
                        <Tooltip
                            title="Reorder down"
                            mouseEnterDelay={1}
                        >
                            <Button
                                icon="arrow-down"
                                size="small"
                                disabled={row.scheduledOrder + 1 - item.rangeStart >= item.pax.length}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    api.data.swapEntities(row.scheduledOrder, row.scheduledOrder + 1)
                                }}
                            />
                        </Tooltip>
                        ,
                        <Tooltip
                            title="Reorder up"
                            mouseEnterDelay={1}
                        >
                            <Button
                                icon="arrow-up"
                                size="small"
                                style={{ marginRight: 6 }}
                                disabled={row.scheduledOrder - 1 - item.rangeStart < 0}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    api.data.swapEntities(row.scheduledOrder, row.scheduledOrder - 1)
                                }}
                            />
                        </Tooltip>
                    ]

                    return (
                        <Button.Group style={{ whiteSpace: 'nowrap' }}>
                            {reorderBtns}
                        </Button.Group>
                    )
                }
            },
            {
                title: (
                    <Tooltip title="Order of Importance" mouseEnterDelay={0.25}>
                        <span
                            style={{
                                cursor: 'help'
                            }}
                        >OI <Icon type="question-circle" /></span>
                    </Tooltip>
                ),
                key: 'oi',
                dataIndex: 'scheduledOrder',
                render: (order) => order + 1,
                width: 60
            },
            {
                title: 'Name',
                key: 'name',
                width: 200,
                render: (_, record) => {
                    let icon = null;
                    const spanClassName = record.currentCarrierID ? BASE_CLS + '-table-on-flight' : '';

                    if (record.currentCarrierID){
                        icon = <Tooltip title="This passenger is currently on a flight. Editing is disabled.">
                            <a
                                href={"/app/scheduling/flight/" + record.currentCarrierID?._id}
                                onClick={(e) => {
                                    e.preventDefault();
                                    history.push("/app/scheduling/flight/" + record.currentCarrierID?._id)
                                }}
                                style={{ marginRight: '6px', position: 'relative' }}
                            >
                                <MCIcon type="helicopter-flight" />
                            </a>
                        </Tooltip>
                    }

                    return (
                        <PaxNameCell
                            record={record}
                            className={spanClassName}
                            prefix={icon}
                            onClick={!editMode ? (r) => api.modal.paxModal.open(r) : undefined}
                            showDeletedPersonNotice
                        />
                    );
                }
            },
            {
                title: 'Notes for Pilot',
                key: 'notesForPilot',
                width: 150,
                render: (_, row) => (
                    <NotesForPilotWrapper
                        entity={row}
                        popupContainer={props.containerElement}
                        api={api}
                        disableEditing={props.disableEditing}
                    />
                )
            },
            {
                title: 'Nation',
                key: 'nation',
                width: 70,
                render: (_, row) => row.personID && row.personID.nation
            },
            {
                title: 'Employer',
                key: 'employer',
                width: 120,
                render: (_, record) => record.employerID && record.employerID.name
            },
            {
                title: 'Pax Wt',
                key: 'paxWeight',
                dataIndex: 'paxWeight',
                width: 50,
                render: (weight: number, row) => {
                    if (!editMode || row.currentCarrierID) return weight || 0;
                    return <span {...stopPropagationProps()}>
                        <InputNumber
                            min={0}
                            style={{
                                maxWidth: 50
                            }}
                            onChange={(value) => handleCellEdit(getSNodeID(row), 'paxWeight', value || 0)}
                            size="small"
                            value={weight || 0}
                            {...stopPropagationProps()}
                        />
                    </span>
                }
            },
            {
                title: 'Bag Wt',
                key: 'bagWeight',
                dataIndex: 'bagWeight',
                width: 50,
                render: (weight, row) => {
                    if (!editMode || row.currentCarrierID) return weight || 0;
                    return <span {...stopPropagationProps()}>
                        <InputNumber
                            min={0}
                            style={{
                                maxWidth: 50
                            }}
                            onChange={(value) => handleCellEdit(getSNodeID(row), 'bagWeight', value || 0)}
                            size="small"
                            value={weight || 0}
                        />
                    </span>
                }
            },
            {
                title: 'Bag Ct',
                key: 'bagCount',
                dataIndex: 'bagCount',
                width: 50,
                render: (count, row) => {
                    if (!editMode || row.currentCarrierID) return count || 0;
                    return <span {...stopPropagationProps()}>
                        <InputNumber
                            min={0}
                            style={{
                                maxWidth: 50
                            }}
                            onChange={(value) => handleCellEdit(getSNodeID(row), 'bagCount', value || 0)}
                            size="small"
                            value={count || 0}
                        />
                    </span>
                }
            },
            {
                title: 'Charge Code',
                key: 'chargeCode',
                dataIndex: 'chargeCode',
                width: 130,
                render: (code: string, row: Phase2ScheduleNode) => {
                    if (!editMode || row.currentCarrierID) return code && code.toUpperCase();

                    const fieldKey = createFormFieldKey(
                        getSNodeID(api.toScheduleNode(row)),
                        'chargeCode'
                    );

                    return (
                        <Form.Item className={FormItemClassNames.NoMargin}>
                            {props.form.getFieldDecorator(fieldKey, {
                                initialValue: code,
                                rules: [
                                    {
                                        required: false,
                                        message: 'Charge code is required',
                                        validator: chargeCodeValidator
                                    }
                                ]
                            })(
                                <Input
                                    min={0}
                                    style={{
                                        minWidth: 70,
                                    }}
                                    placeholder={String(api.chargeCodes.getDefault()).toUpperCase()}
                                    {...stopPropagationProps()}
                                    size="small"
                                />
                            )}
                        </Form.Item>
                    )
                }
            },
            // {
            //     title: 'ISN Status',
            //     key: 'isnStatus',
            //     render: (_, row) => {
            //
            //         if (row.transitType !== 'OUTBOUND') return '—';
            //
            //         const pax = {
            //             ...row,
            //             isnCheckError: api.bulkIsnChecker.checkError(getSNodeID(row)),
            //             isnCheckRunning: api.bulkIsnChecker.checkLoading(getSNodeID(row._id))
            //         }
            //
            //         return <IsnCheckStatusButton
            //             style={{
            //                 pointerEvents: editMode ? 'none' : undefined,
            //                 minWidth: 120,
            //                 maxWidth: 200
            //             }}
            //             pax={pax}
            //             size="small"
            //             block
            //             checking={pax.isnCheckRunning}
            //             onClick={(e) => {
            //                 e.stopPropagation();
            //                 api.drawer.isnDrawer.open(row);
            //             }}
            //         />
            //     }
            // },
            {
                title: 'Contractor',
                key: 'contractor',
                width: 130,
                render: (_, row) => {
                    if (!editMode || row.currentCarrierID) {
                        return row.contractorDoc ? row.contractorDoc.name : '—';
                    }
                    return <div onClick={(e) => e.stopPropagation()}>
                        <OrganizationSelect
                            {...stopPropagationProps()}
                            size="small"
                            style={{
                                width: '100%'
                            }}
                            dropdownMatchSelectWidth={false}
                            classTypes={['flytsuite.employer']}
                            labelInValue
                            value={getLabelInValue(row.contractorDoc, 'name')}
                            onChange={(value: LabelInValue) => handleCellEdit(getSNodeID(row), 'contractorDoc', value ? { _id: value.key, name: value.label as string } : null)}
                        />
                    </div>
                }
            },
            {
                title: 'Compliance',
                key: 'compliance',
                render: (_, row) => {
                    if (row.transitType !== 'OUTBOUND') return '—';

                    const pax = {
                        ...row
                    }

                    const loading = api.bulkComplianceChecker.isLoading(getSNodeID(row));

                    return <ComplianceStatusButton
                        style={{
                            pointerEvents: editMode ? 'none' : undefined,
                            minWidth: 120,
                            maxWidth: 200
                        }}
                        pax={pax}
                        size="small"
                        block
                        checking={loading}
                    />
                }
            },
            {
                title: 'Safety Card Expiration',
                key: 'safetyCard',
                width: 175,
                render: (_, row) => <SafetyCardStatus credentials={row.personID?.credentials} />
            },
            {
                title: 'Requested Check-in Time',
                key: 'requestedCheckInTime',
                dataIndex: 'requestedCheckInTime',
                width: 150,
                render: (checkInTime, row) => {

                    const checkInMoment = checkInTime ? moment.tz(checkInTime, 'HH:mm:ss', 'America/Chicago') : null;

                    if (!checkInMoment) return '—';

                    return <span style={{ whiteSpace: 'nowrap' }}>{checkInMoment.format('HH:mm') + ' CST'}</span>
                }
            },
            {
                title: 'Notified At',
                key: 'notifiedCheckIn',
                dataIndex: 'lastCheckInNotificationEvent',
                render: (event, row) => {
                    if (!event) return '—';

                    if (!event.success){
                        return (
                            <Tooltip title={
                                <Typography.Text type="danger">
                                    {`Notification failed: ${event.errorMessage || 'Unknown error'}`}
                                </Typography.Text>
                            }>
                                <Typography.Text type="danger">ERROR <Icon type="down" /></Typography.Text>
                            </Tooltip>
                        )
                    }

                    const eventMoment = event.createdTs ? moment.tz(event.createdTs, 'America/Chicago') : null;

                    if (!eventMoment) return '—';

                    return <span style={{ whiteSpace: 'nowrap' }}>{eventMoment.format('YYYY-MM-DD HH:mm') + ' CST'}</span>
                }
            },
            {
                title: 'Created By',
                key: 'createdBy',
                dataIndex: 'createdBy'
            }
        ]

        if (!showSafetyCard) {
            columns = columns.filter((column) => column.key !== 'safetyCard');
        }

        if (editMode){
            columns = columns.filter((col) => !['notifiedCheckIn', 'requestedCheckInTime'].includes(col.key as string));
        }

        if (typeof props.modifyPaxColumns === 'function'){
            columns = props.modifyPaxColumns(api, columns);
        }

        if (!item.pax.length) return null;

        return <ETable
            className={BASE_CLS + '-table'}
            scroll={{
                x: 'max-content'
            }}
            dataSource={item.pax}
            columns={columns}
            rowSelection={selectionEnabled ? ({
                selectedRowKeys: api.selection.entityIds.get(),
                onChange: api.selection.entityIds.set,
                columnWidth: 32
            }) : null}
            onRow={(record: ScheduleNode) => {
                return {
                    onClick: () => {
                        if (!selectionEnabled) return;
                        api.selection.entityIds.toggle(getSNodeID(record));
                    },
                    className: selectionEnabled ? BASE_CLS + '-table-row' : '',
                    disabled: !!record.currentCarrierID && !selectionEnabled
                }
            }}
            pagination={false}
            rowKey={record => getSNodeID(record)}
            size="small"
        />
    }

    function renderCgoTable(item: DepDestListItem){

        const cgo = item.cgo.filter(e => e.classType === 'flytsuite.cgonode');

        let columns: ColumnProps<any>[] = [
            {
                title: <strong><i>CGO ({cgo.length})</i></strong>,
                key: 'action',
                width: 73,
                fixed: 'left' as 'left',
                render: (_, row) => {

                    if (!editMode) return (
                        <div className={BASE_CLS + '-icon-container'}>
                            <MCIcon type="cargo" />
                        </div>
                    );

                    const reorderBtns = [
                        <Tooltip
                            title="Reorder down"
                            mouseEnterDelay={1}
                        >
                            <Button
                                icon="arrow-down"
                                size="small"
                                disabled={row.scheduledOrder + 1 - item.rangeStart >= item.all.length}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    api.data.swapEntities(row.scheduledOrder, row.scheduledOrder + 1);
                                }}
                            />
                        </Tooltip>
                        ,
                        <Tooltip
                            title="Reorder up"
                            mouseEnterDelay={1}
                        >
                            <Button
                                icon="arrow-up"
                                size="small"
                                style={{ marginRight: 6 }}
                                disabled={row.scheduledOrder - 1 - item.rangeStart < item.pax.length}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    api.data.swapEntities(row.scheduledOrder, row.scheduledOrder - 1);
                                }}
                            />
                        </Tooltip>
                    ]

                    return (
                        <Button.Group style={{ whiteSpace: 'nowrap' }}>
                            {reorderBtns}
                        </Button.Group>
                    )
                }
            },
            {
                title: (
                    <Tooltip title="Order of Importance" mouseEnterDelay={0.25}>
                        <span
                            style={{
                                cursor: 'help'
                            }}
                        >OI <Icon type="question-circle" /></span>
                    </Tooltip>
                ),
                key: 'oi',
                dataIndex: 'scheduledOrder',
                render: (order) => order + 1,
                width: 60
            },
            {
                title: 'Name',
                key: 'name',
                width: 200,
                render: (_, record) => {
                    let spanClassName = record.currentCarrierID ? BASE_CLS + '-table-on-flight' : '';

                    if (record.currentCarrierID){
                        return <span className={spanClassName}>
                            <a
                                href={"/app/scheduling/flight/" + record.currentCarrierID?._id}
                                onClick={(e) => {
                                    e.preventDefault();
                                    history.push("/app/scheduling/flight/" + record.currentCarrierID?._id)
                                }}
                                style={{marginRight: '6px', position: 'relative'}}
                            >
                                <Tooltip title="This cargo is currently on a flight. Editing is disabled.">
                                    <MCIcon type="helicopter-flight" style={{marginRight: '6px', position: 'relative'}}/>
                                </Tooltip>
                            </a>
                            <span>{record.name}</span>
                        </span>
                    }

                    return record.name
                }
            },
            {
                title: 'Notes for Pilot',
                key: 'notesForPilot',
                width: 150,
                render: (_, row) => (
                    <NotesForPilotWrapper
                        entity={row}
                        popupContainer={props.containerElement}
                        api={api}
                    />
                )
            },
            {
                title: 'Weight',
                key: 'weight',
                dataIndex: 'weight',
                width: 70,
                render: (weight, row) => {
                    if (!editMode || row.currentCarrierID) return weight || 0;
                    return <span {...stopPropagationProps()}>
                        <InputNumber
                            min={0}
                            onChange={(value) => handleCellEdit(getSNodeID(row), 'weight', value || 0)}
                            size="small"
                            value={weight || 0}
                        />
                    </span>
                }
            },
            {
                title: 'HazMat',
                key: 'hazmat',
                width: 120,
                render: (_, record) => record.hazmat ? record.hazmatUnCode || "YES" : null
            },
            {
                title: 'Attention',
                key: 'attentionTo',
                width: 200,
                render: (_, record) => record.attentionTo
            },
            {
                title: 'Initials',
                key: 'initials',
                width: 130,
                render: (_, record) => record.initials
            },
            {
                title: 'Notes',
                key: 'notes',
                render: (_, record) => record.notes
            },
            {
                title: 'Created By',
                key: 'createdBy',
                dataIndex: 'createdBy'
            }
        ]

        if (typeof props.modifyCgoColumns === 'function'){
            columns = props.modifyCgoColumns(api, columns);
        }

        if (!cgo.length) return null;

        return <ETable
            className={BASE_CLS + '-table'}
            scroll={{
                x: 'max-content'
            }}
            dataSource={cgo}
            columns={columns}
            rowSelection={selectionEnabled ? ({
                selectedRowKeys: api.selection.entityIds.get(),
                onChange: api.selection.entityIds.set,
                columnWidth: 32
            }) : null}
            onRow={(record: ScheduleNode, index) => {
                const onRowResult = typeof props.onRow === 'function' ? props.onRow(record, index) : {};
                return {
                    onClick: () => {
                        if (!selectionEnabled) return;
                        api.selection.entityIds.toggle(getSNodeID(record));
                    },
                    className: selectionEnabled ? BASE_CLS + '-table-row' : '',
                    disabled: !!record.currentCarrierID && !selectionEnabled,
                    ...onRowResult
                }
            }}
            pagination={false}
            rowKey={record => getSNodeID(record)}
            size="small"
        />
    }

    function renderPaxModal(){
        const modalState = api.modal.paxModal.get();
        const modalActions = api.modal.paxModal;
        return (
            <Modal
                title="Edit Passenger"
                visible={modalState.visible}
                onCancel={modalActions.close}
                footer={null}
                width="50rem"
            >
                <PassengerEditor
                    title="Edit Passenger"
                    edit={true}
                    data={modalState.pax}
                    onCancel={modalActions.close}
                />
            </Modal>
        )
    }

    const panels = data.map(renderPanel);

    let noDataMsg = null;
    let collapse = null;
    if (api.data.modifiedEntities.get().length) {
        collapse = (
            <Collapse
                className={BASE_CLS + '-table-collapse'}
                activeKey={api.editor.visibleLegs.getAsStrings()}
                onChange={api.editor.visibleLegs.setAsStrings}
            >
                {panels}
            </Collapse>
        )
    }
    else if (editMode && !api.queryInfo.loading)
    {
        noDataMsg = <NonIdealState
            title="Add Passengers and Cargo"
            description={
                <>
                    <p>Use the left panel to add PAX/CGO</p>
                    {api.carriableSelector.hideCarriablePanel.get() ? (
                        <Button
                            icon="menu-unfold"
                            type="primary"
                            onClick={api.carriableSelector.hideCarriablePanel.toggle}
                        >Show Panel</Button>
                    ) : null}
                </>
            }
            icon="usergroup-add"
            style={{ flex: 1 }}
        />
    }
    else if (!api.queryInfo.loading) {
        noDataMsg = <NonIdealState
            title="No Data"
            description="This schedule has no PAX/CGO"
            icon="exclamation-circle"
            style={{ flex: 1 }}
        />
    }

    return <>
        {collapse}
        {noDataMsg}
        {renderPaxModal()}
    </>
}

const Phase2Table = Form.create<Phase2TablePropsWithForm>({

    // Consumes form data from state manager and converts it into fields for each table row.
    mapPropsToFields({ api }){
        const entityIds = api.forms.entityForm.listEntityIds();

        const fields = {};

        entityIds.forEach((entityId) => {
            const formData = api.forms.entityForm.get(entityId);

            Object.entries(formData).forEach(([ key, value ]) => {
                fields[createFormFieldKey(entityId, key)] = createFormField(value)
            });
        })

        return fields;
    },

    // Converts individual form fields into the structure that the parent state manager expects
    onFieldsChange({ api }, _, allFields){

        const entityFormData: { [entityId: string]: Partial<Phase2EntityFormData> } = {};

        // Initialize entityFormData values
        Object.keys(allFields).forEach((key) => {
            const result = splitFormFieldKey(key);
            if (result){
                entityFormData[result.entityId] = {}
            }
        })

        // Add fields and values to each entityId
        Object.entries(allFields).forEach(([key, value]) => {
            const result = splitFormFieldKey(key);
            if (result){
                entityFormData[result.entityId][result.fieldName] = value;
            }
        })

        // Send final result to state manager
        Object.entries(entityFormData).forEach(([entityId, formData]) => {
            api.forms.entityForm.set(entityId, formData);
        })
    }
})(Phase2TableBase)

export default Phase2Table
